import { useState, useEffect } from 'react';
import { Settings, DollarSign, Calendar, Save, FileText, Percent } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { revenueAPI } from '@/services/api';

const RevenueSettingsPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    vendor_registration_fee: 0,
    vendor_registration_tax_percent: 18,
    brand_registration_fee: 0,
    brand_registration_tax_percent: 18,
    barcode_change_fee: 0,
    barcode_change_tax_percent: 18,
    invoice_generate_day: 3,
    invoice_prefix: 'RVINV'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await revenueAPI.getHQSettings();
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await revenueAPI.updateHQSettings(settings);
      toast({ title: 'Success', description: 'Revenue settings updated successfully' });
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save settings', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="revenue-settings-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <DollarSign className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Revenue Settings</h1>
            <p className="text-gray-500 text-sm">Configure fees and charges for vendor services</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vendor Registration */}
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              Vendor Registration Fee
            </CardTitle>
            <CardDescription>One-time fee charged to vendors at registration</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Fee Amount (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  value={settings.vendor_registration_fee}
                  onChange={(e) => updateField('vendor_registration_fee', parseFloat(e.target.value) || 0)}
                  className="bg-gray-50 border-gray-300"
                  data-testid="vendor-reg-fee-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Tax (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={settings.vendor_registration_tax_percent}
                  onChange={(e) => updateField('vendor_registration_tax_percent', parseFloat(e.target.value) || 0)}
                  className="bg-gray-50 border-gray-300"
                />
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Total:</strong> ₹{(settings.vendor_registration_fee * (1 + settings.vendor_registration_tax_percent / 100)).toFixed(2)} (incl. tax)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Brand Registration */}
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-500" />
              Brand Registration Fee
            </CardTitle>
            <CardDescription>One-time fee charged to manufacturers for new brands</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Fee Amount (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  value={settings.brand_registration_fee}
                  onChange={(e) => updateField('brand_registration_fee', parseFloat(e.target.value) || 0)}
                  className="bg-gray-50 border-gray-300"
                  data-testid="brand-reg-fee-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Tax (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={settings.brand_registration_tax_percent}
                  onChange={(e) => updateField('brand_registration_tax_percent', parseFloat(e.target.value) || 0)}
                  className="bg-gray-50 border-gray-300"
                />
              </div>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-700">
                <strong>Total:</strong> ₹{(settings.brand_registration_fee * (1 + settings.brand_registration_tax_percent / 100)).toFixed(2)} (incl. tax)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Barcode Change Fee */}
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" />
              Additional Barcode Fee
            </CardTitle>
            <CardDescription>Fee for adding 2nd+ barcode to a product (1st is free)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Fee Amount (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  value={settings.barcode_change_fee}
                  onChange={(e) => updateField('barcode_change_fee', parseFloat(e.target.value) || 0)}
                  className="bg-gray-50 border-gray-300"
                  data-testid="barcode-fee-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Tax (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={settings.barcode_change_tax_percent}
                  onChange={(e) => updateField('barcode_change_tax_percent', parseFloat(e.target.value) || 0)}
                  className="bg-gray-50 border-gray-300"
                />
              </div>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg">
              <p className="text-sm text-amber-700">
                <strong>Total:</strong> ₹{(settings.barcode_change_fee * (1 + settings.barcode_change_tax_percent / 100)).toFixed(2)} (incl. tax)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Invoice Settings */}
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-teal-500" />
              Invoice Generation Settings
            </CardTitle>
            <CardDescription>Configure monthly invoice generation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Generate On Day</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={settings.invoice_generate_day}
                  onChange={(e) => updateField('invoice_generate_day', parseInt(e.target.value) || 3)}
                  className="bg-gray-50 border-gray-300"
                  data-testid="invoice-day-input"
                />
                <p className="text-xs text-gray-500">Day of month (1-28) for auto-generating rental invoices</p>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Invoice Prefix</Label>
                <Input
                  value={settings.invoice_prefix}
                  onChange={(e) => updateField('invoice_prefix', e.target.value)}
                  placeholder="RVINV"
                  className="bg-gray-50 border-gray-300"
                />
                <p className="text-xs text-gray-500">Format: {settings.invoice_prefix}-2026-00001</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
        <CardContent className="p-4">
          <h3 className="font-semibold text-emerald-800 mb-2">Revenue Streams Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div className="p-3 bg-white/70 rounded-lg">
              <p className="font-medium text-gray-700">Vendor Registration</p>
              <p className="text-gray-500">One-time • All Stores • Paid by Vendor</p>
            </div>
            <div className="p-3 bg-white/70 rounded-lg">
              <p className="font-medium text-gray-700">Brand Registration</p>
              <p className="text-gray-500">One-time • All Stores • Paid by Manufacturer</p>
            </div>
            <div className="p-3 bg-white/70 rounded-lg">
              <p className="font-medium text-gray-700">Additional Barcode</p>
              <p className="text-gray-500">One-time • All Stores • Paid by Manufacturer</p>
            </div>
            <div className="p-3 bg-white/70 rounded-lg">
              <p className="font-medium text-gray-700">Product Registration</p>
              <p className="text-gray-500">One-time • Store-wise • Paid by Manufacturer</p>
              <p className="text-xs text-gray-400 mt-1">(Configured per store)</p>
            </div>
            <div className="p-3 bg-white/70 rounded-lg">
              <p className="font-medium text-gray-700">Shelf/Rack Rental</p>
              <p className="text-gray-500">Monthly • Store-wise • Paid by Manufacturer</p>
              <p className="text-xs text-gray-400 mt-1">(Configured per store)</p>
            </div>
            <div className="p-3 bg-white/70 rounded-lg">
              <p className="font-medium text-gray-700">Display Ad Rental</p>
              <p className="text-gray-500">Monthly • Store-wise • Paid by Manufacturer</p>
              <p className="text-xs text-gray-400 mt-1">(Configured per store)</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RevenueSettingsPage;
